import { AssociationType } from "@/shared/domain/association";
import { EventRecord } from "@/shared/domain/events";
import { FileRecord } from "@/shared/domain/files";
import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";
import {
  RobotoPrincipal,
  RobotoPrincipalType,
} from "@/shared/principal/RobotoPrincipal";
import { nanoSecToDate } from "@/shared/time";
import { CellType, Dataset, Row } from "@/types";

export function isDataset(object: unknown): object is Dataset {
  return (object as Dataset).dataset_id !== undefined;
}

export function isFile(object: unknown): object is FileRecord {
  return (object as FileRecord).file_id !== undefined;
}

function isMsgPath(object: unknown): object is MessagePathRecord {
  return (object as MessagePathRecord).message_path_id !== undefined;
}

export function isTopic(object: unknown): object is TopicRecord {
  return (object as TopicRecord).topic_id !== undefined && !isMsgPath(object);
}

export function isEvent(object: unknown): object is EventRecord {
  return (object as EventRecord).event_id !== undefined;
}

function toDisplayable(value: unknown): string {
  switch (typeof value) {
    case "undefined":
      return "Not set";
    case "string":
      return value;
    case "number":
      return value.toString();
    case "boolean":
      return value.toString();
    case "object":
      if (value === null) {
        return "null";
      }
      return JSON.stringify(value);
    default:
      return "Unknown";
  }
}

const principalTypeToCellType = (type: RobotoPrincipalType): CellType => {
  switch (type) {
    case RobotoPrincipalType.Device:
      return "device";
    case RobotoPrincipalType.Invocation:
      return "invocation";
    case RobotoPrincipalType.User:
      return "email";
    case RobotoPrincipalType.Integration:
      return "integration";
    default:
      return "simpleText";
  }
};

export const pushMetadataCell = (
  row: Row,
  column: string,
  item: Dataset | FileRecord | TopicRecord | MessagePathRecord | EventRecord,
  entityId: string,
  getMetadataValue?: (metadataColumn: string, entityId: string) => unknown,
) => {
  const cellId = row.id + "-" + column;
  const fieldName = column.includes("metadata.")
    ? column.split("metadata.")[1] // extract the actual field name
    : column;

  let value = item?.metadata?.[fieldName];
  if ((value === undefined || value === null) && getMetadataValue) {
    value = getMetadataValue(column, entityId);
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    row.cells.push({
      type: "simpleText",
      data: value,
      id: cellId,
    });
  } else if (Array.isArray(value)) {
    if (value.length === 0 || typeof value[0] !== "object") {
      row.cells.push({
        type: "simpleText",
        data: value,
        id: cellId,
      });
    } else {
      // Don't try to render arrays of complex types.
      row.cells.push({
        type: "simpleText",
        data: "",
        id: cellId,
      });
    }
  } else {
    row.cells.push({
      type: "simpleText",
      data: "",
      id: cellId,
    });
  }
};

// This function creates cell content from row values
// It also ensures that certain fields are formatted correctly
// where appropriate, e.g. IDs, users, tags etc.
export const pushStandardCell = (
  row: Row,
  column: string,
  item: Dataset | FileRecord | TopicRecord | MessagePathRecord | EventRecord,
) => {
  const cellId = `${row.id}-${column}`;
  // Always format audit fields as principals if possible
  if (column === "created_by" || column === "modified_by") {
    let sourceType: CellType = "email";
    let sourceData = item[column];

    const principal = RobotoPrincipal.fromString(sourceData);

    if (principal) {
      sourceData = principal.unqualifiedId;
      sourceType = principalTypeToCellType(principal.ptype);
    }

    row.cells.push({
      type: sourceType,
      data: sourceData,
      id: cellId,
    });
    return;
  }

  if (isDataset(item)) {
    let sourceType: CellType = "email";
    let sourceData = item.created_by;

    const principal = RobotoPrincipal.fromString(item.created_by);

    if (principal) {
      sourceData = principal.unqualifiedId;
      sourceType = principalTypeToCellType(principal.ptype);
    }

    if (item.device_id) {
      sourceData = item.device_id;
      sourceType = "device";
    }

    switch (column) {
      case "dataset_id":
        row.cells.push({
          type: "id",
          data: item[column] ?? "",
          id: cellId,
        });
        return;
      case "tags":
        row.cells.push({
          type: "chips",
          data: (item as Dataset | FileRecord)?.tags ?? [],
          id: cellId,
        });
        return;
      case "name":
        row.cells.push({
          type: "simpleText",
          data: item[column] || "N/A",
          id: cellId,
        });
        return;
      case "device_id":
        row.cells.push({
          type: "device",
          data: item[column] ?? "",
          id: cellId,
        });
        return;
      case "source":
        row.cells.push({
          type: sourceType,
          data: sourceData,
          id: cellId,
        });
        return;
    }
  }

  if (isFile(item)) {
    let sourceType: CellType = "email";
    let sourceData = item.created_by;

    const principal = RobotoPrincipal.fromString(item.created_by);

    if (principal) {
      sourceData = principal.unqualifiedId;
      sourceType = principalTypeToCellType(principal.ptype);
    }

    switch (column) {
      case "file_id":
        row.cells.push({
          type: "id",
          data: item[column] ?? "",
          id: cellId,
        });
        return;
      case "relative_path":
        row.cells.push({
          type: "id",
          data: item[column] ?? "",
          id: cellId,
          association: {
            association_id: item.file_id,
            association_type: AssociationType.File,
          },
        });
        return;
      case "association_id":
        row.cells.push({
          type: "id",
          data: item[column] ?? "",
          id: cellId,
        });
        return;
      case "tags":
        row.cells.push({
          type: "chips",
          data: (item as Dataset | FileRecord)?.tags ?? [],
          id: cellId,
        });
        return;
      case "device_id":
        row.cells.push({
          type: "device",
          data: item[column] ?? "",
          id: cellId,
        });
        return;
      case "source":
        row.cells.push({
          type: sourceType,
          data: sourceData,
          id: cellId,
        });
        return;
    }
  }

  if (isTopic(item)) {
    switch (column) {
      case "topic_id":
        row.cells.push({
          type: "id",
          data: item[column],
          id: cellId,
          association: item.association,
        });
        return;
    }
  }

  if (isMsgPath(item)) {
    switch (column) {
      case "message_path_id":
        row.cells.push({
          type: "id",
          data: item[column],
          id: cellId,
        });
        return;
      case "message_path":
        row.cells.push({
          type: "simpleText",
          data: item.source_path,
          id: cellId,
        });
        return;
    }
  }

  if (isEvent(item)) {
    switch (column) {
      case "event_id":
        row.cells.push({
          type: "id",
          data: item[column],
          id: cellId,
        });
        return;

      case "tags":
        row.cells.push({
          type: "chips",
          data: item.tags,
          id: cellId,
        });
        return;

      case "start_time":
      case "end_time": // FALLTHROUGH
        row.cells.push({
          type: "datetime",
          data: nanoSecToDate(item[column]).toLocaleString(),
          id: cellId,
        });
        return;
    }
  }

  // Applicable to a mixture of objects
  switch (column) {
    case "created":
    case "modified": // FALLTHROUGH
      row.cells.push({
        type: "datetime",
        data: item[column] ?? "",
        id: cellId,
      });
      return;
    case "created_by":
    case "modified_by": // FALLTHROUGH
      row.cells.push({
        type: "email",
        data: item[column] ?? "",
        id: cellId,
      });
      return;
    // FALLTHROUGH
    default: {
      row.cells.push({
        type: "simpleText",
        data: toDisplayable((item as Record<string, unknown>)[column]),
        id: cellId,
      });
    }
  }
};

/**
 * Merges the metadata columns from the keys API with the metadata keys from the existing page of data.
 * There could be a difference between the two, e.g. the API may not have a key the user just added, because
 * the materialized view backing the API is not refreshed immediately.
 */
export const mergeMetadataColumns = (
  data:
    | Dataset[]
    | FileRecord[]
    | TopicRecord[]
    | MessagePathRecord[]
    | EventRecord[]
    | null,
  metadataKeys: string[],
): string[] | null => {
  const columnSet = new Set<string>();

  if (metadataKeys.length > 0) {
    metadataKeys.forEach((key) => columnSet.add(`metadata.${key}`));
  }

  if (data === null || data === undefined) {
    return null;
  }

  for (let i = 0; i < data.length; i++) {
    const { metadata } = data[i];

    if (!metadata) {
      continue;
    }

    Object.keys(metadata).forEach((key) => columnSet.add(`metadata.${key}`));
  }

  return Array.from(columnSet);
};

export const createRows = (
  data:
    | Dataset[]
    | FileRecord[]
    | TopicRecord[]
    | MessagePathRecord[]
    | EventRecord[]
    | null,
  columns: string[],
  possibleMetadataColumns: string[],
  getMetadataValue?: (metadataColumn: string, entityId: string) => unknown,
): Row[] => {
  const rows: Row[] = [];

  if (data === null) {
    return [];
  }

  // create a row for each
  for (let i = 0; i < data.length; i++) {
    let entityId: string;
    const rowData = data[i];

    if (isDataset(rowData)) {
      entityId = rowData.dataset_id;
    } else if (isFile(rowData)) {
      entityId = rowData.file_id;
    } else if (isTopic(rowData)) {
      entityId = rowData.topic_id;
    } else if (isMsgPath(rowData)) {
      entityId = rowData.message_path_id;
    } else if (isEvent(rowData)) {
      entityId = rowData.event_id;
    } else {
      continue;
    }

    const row: Row = {
      id: entityId,
      cells: [],
    };

    columns.forEach((column) => {
      if (possibleMetadataColumns.includes(column)) {
        pushMetadataCell(row, column, rowData, entityId, getMetadataValue);
      } else {
        pushStandardCell(row, column, rowData);
      }
    });

    rows.push(row);
  }

  return rows;
};
